import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { SEO } from '@/components/seo/seo';
import { CouncilPackProof } from '@/components/claims/council-pack-proof';
import { api } from '@/lib/api';
import {
  createGuestDraft,
  readCouncilHint,
  saveGuestDraft,
  type GuestDamageCategory,
} from '@/lib/guest-draft';
import {
  setGuestCheckFunnelVariant,
  trackAccountSaveRequested,
  trackEligibilityResultViewed,
  trackEligibilityStarted,
  trackEligibilityStepCompleted,
} from '@/lib/analytics';
import { useAuth } from '@/contexts/auth-context';

type ResultEnum = 'authority_likely' | 'authority_uncertain';
type RatingBand = 'green' | 'amber' | 'red' | 'not_scored';

interface PreviewResponse {
  result: ResultEnum;
  authority: {
    name: string;
    type: string;
    responsibility: string;
    slug?: string;
  };
  dftContext?: {
    band: RatingBand;
    period: string;
    summary: string;
  };
  checklist: string[];
}

const damageOptions: { value: GuestDamageCategory; label: string }[] = [
  { value: 'tyre', label: 'Tyre' },
  { value: 'wheel', label: 'Wheel or alloy' },
  { value: 'suspension', label: 'Suspension' },
  { value: 'bodywork', label: 'Bodywork' },
  { value: 'steering', label: 'Steering' },
  { value: 'other', label: 'Other vehicle damage' },
];

const checklistCopy: Record<string, string> = {
  confirm_road_manager: 'Confirm the exact road manager for the specific road.',
  photograph_defect: 'Photograph the defect, its position and useful measurements.',
  photograph_damage: 'Photograph the vehicle damage clearly.',
  keep_repair_records: 'Keep repair quotes, invoices and proof of payment.',
  record_incident_details: 'Record the time, exact road location and what happened.',
};

function normalisePostcode(value: string): string | null {
  const compact = value.replace(/\s/g, '').toUpperCase();
  const match = compact.match(/^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{2})$/);
  return match ? `${match[1]} ${match[2]}` : null;
}

export function CheckPage() {
  const { isAuthenticated } = useAuth();
  const { search } = useLocation();
  const councilHint = readCouncilHint(search);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [incidentDate, setIncidentDate] = useState('');
  const [damageCategory, setDamageCategory] = useState<GuestDamageCategory | ''>('');
  const [postcodeInput, setPostcodeInput] = useState('');
  const [result, setResult] = useState<PreviewResponse | null>(null);
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    setGuestCheckFunnelVariant();
    trackEligibilityStarted();
  }, []);

  const continueIncident = () => {
    if (!incidentDate || !damageCategory) return;
    trackEligibilityStepCompleted('incident');
    setStep(2);
  };

  const submitLocation = async () => {
    const postcode = normalisePostcode(postcodeInput);
    if (!postcode || !incidentDate || !damageCategory) {
      setError(true);
      return;
    }

    const draft = createGuestDraft({
      incidentDate,
      damageCategory,
      locationPostcode: postcode,
      ...(councilHint && { councilHint }),
    });
    saveGuestDraft(draft);
    trackEligibilityStepCompleted('location');
    setIsLoading(true);
    setError(false);

    try {
      const preview = await api.post<PreviewResponse>(
        '/api/eligibility/preview',
        {
          postcode,
          ...(councilHint && { councilHint }),
        },
        { auth: false }
      );
      setResult(preview);
      setStep(3);
      trackEligibilityResultViewed({
        result: preview.result,
        ...(preview.authority.slug && { authoritySlug: preview.authority.slug }),
        ...(preview.dftContext && { ratingBand: preview.dftContext.band }),
      });
    } catch {
      setResult(null);
      setStep(3);
      setError(true);
      trackEligibilityResultViewed({ result: 'authority_uncertain' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Preliminary pothole claim check"
        description="Check the likely highway authority and the evidence you may need before creating an account."
        noindex
      />
      <main className="mx-auto max-w-3xl px-4 py-12">
        <p className="text-sm font-semibold uppercase tracking-wide text-brand-600">
          Free preliminary route and evidence check
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-brand-950">
          Check where your pothole claim may need to go
        </h1>
        <p className="mt-4 text-surface-700">
          No account is needed for the result. We only ask for incident basics and the
          road location postcode.
        </p>

        <ol className="mt-8 flex gap-3 text-sm" aria-label="Check progress">
          {['Incident', 'Location', 'Result'].map((label, index) => (
            <li
              key={label}
              className={step >= index + 1 ? 'font-semibold text-brand-700' : 'text-surface-500'}
            >
              {index + 1}. {label}
            </li>
          ))}
        </ol>

        <section className="mt-8 rounded-2xl border border-surface-200 bg-white p-6 shadow-sm">
          {step === 1 && (
            <>
              <h2 className="font-display text-2xl font-semibold text-brand-950">
                Incident basics
              </h2>
              <label className="mt-6 block font-medium text-brand-950" htmlFor="incident-date">
                Incident date
              </label>
              <input
                id="incident-date"
                type="date"
                value={incidentDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(event) => setIncidentDate(event.target.value)}
                className="mt-2 w-full rounded-lg border border-surface-300 p-3"
              />
              <fieldset className="mt-6">
                <legend className="font-medium text-brand-950">Damage category</legend>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  {damageOptions.map((option) => (
                    <label
                      key={option.value}
                      className="rounded-lg border border-surface-200 p-3"
                    >
                      <input
                        type="radio"
                        name="damage-category"
                        value={option.value}
                        checked={damageCategory === option.value}
                        onChange={() => setDamageCategory(option.value)}
                      />{' '}
                      {option.label}
                    </label>
                  ))}
                </div>
              </fieldset>
              <button
                type="button"
                onClick={continueIncident}
                className="mt-6 rounded-lg bg-brand-700 px-5 py-3 font-semibold text-white"
              >
                Continue
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-display text-2xl font-semibold text-brand-950">
                Road location
              </h2>
              <label className="mt-6 block font-medium text-brand-950" htmlFor="road-postcode">
                Road location postcode
              </label>
              <input
                id="road-postcode"
                autoComplete="postal-code"
                value={postcodeInput}
                onChange={(event) => {
                  setPostcodeInput(event.target.value);
                  setError(false);
                }}
                className="mt-2 w-full rounded-lg border border-surface-300 p-3 uppercase"
              />
              <p className="mt-3 text-sm text-surface-700">
                Your postcode is used transiently to identify the likely authority. Fixtyer
                does not save it unless you later create and save a claim.
              </p>
              {error && <p className="mt-3 text-error-600">Enter a valid UK postcode.</p>}
              <button
                type="button"
                disabled={isLoading}
                onClick={submitLocation}
                className="mt-6 rounded-lg bg-brand-700 px-5 py-3 font-semibold text-white disabled:opacity-60"
              >
                {isLoading ? 'Checking…' : 'Check likely authority'}
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-display text-2xl font-semibold text-brand-950">
                Your preliminary check
              </h2>
              {result ? (
                <>
                  <p className="mt-5 text-sm text-surface-700">Likely local highway authority</p>
                  <p className="text-xl font-semibold text-brand-950">{result.authority.name}</p>
                  <p className="mt-3 text-surface-700">
                    A postcode can identify the likely local authority, but the specific road
                    could be managed by National Highways, Transport for London, the Welsh
                    Government, a private owner or another body. Confirm the road manager.
                  </p>
                  {result.dftContext && (
                    <div className="mt-5 rounded-xl bg-surface-50 p-4">
                      <p className="font-semibold text-brand-950">
                        Published DfT context: {result.dftContext.band.replace('_', ' ')}
                      </p>
                      <p className="mt-1 text-sm text-surface-700">
                        {result.dftContext.summary} This public authority-level context does
                        not establish what happened on a particular road.
                      </p>
                    </div>
                  )}
                  <h3 className="mt-6 font-display text-xl font-semibold text-brand-950">
                    Evidence to prepare
                  </h3>
                  <ul className="mt-3 list-disc space-y-2 pl-5 text-surface-700">
                    {result.checklist.map((item) => (
                      <li key={item}>{checklistCopy[item] ?? item}</li>
                    ))}
                  </ul>
                </>
              ) : (
                <div className="mt-5">
                  <p className="font-semibold text-brand-950">
                    We could not identify a likely authority from that postcode.
                  </p>
                  <p className="mt-2 text-surface-700">
                    Check the postcode or try again. You can also confirm the road manager
                    through the relevant official authority route.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setError(false);
                      setStep(2);
                    }}
                    className="mt-4 font-semibold text-brand-700 underline"
                  >
                    Try again
                  </button>
                </div>
              )}

              <div className="mt-6 rounded-xl border border-warning-300 bg-warning-50 p-4 text-sm text-surface-800">
                This is a preliminary route and evidence check—not legal advice, not proof
                of liability, and not a claim-success prediction.
              </div>
              <p className="mt-4 text-sm text-surface-700">
                The postcode is used transiently for this lookup and is not saved by
                Fixtyer unless you create and save a claim.
              </p>
              <Link
                to={isAuthenticated ? '/claims/new' : '/register?continue=%2Fclaims%2Fnew'}
                onClick={() => {
                  if (!isAuthenticated) trackAccountSaveRequested();
                }}
                className="mt-6 inline-block rounded-lg bg-brand-700 px-5 py-3 font-semibold text-white"
              >
                {isAuthenticated ? 'Save and continue' : 'Create a free account to save and continue'}
              </Link>
              <p className="mt-4 text-sm text-surface-700">
                See what the £35 Council Pack contains in our{' '}
                <Link className="font-semibold text-brand-700 underline" to="/sample-council-claim-pack">
                  illustrative synthetic sample
                </Link>.
              </p>
            </>
          )}
        </section>
        <div className="mt-8">
          <CouncilPackProof />
        </div>
      </main>
    </>
  );
}
